import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PawPrint, LogIn, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Seo } from "@/components/Seo";

export default function AdminLogin() {
  const { login } = useAuth();
  const { t } = useLang();
  const a = t.admin;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || a.login_error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-brand-soft/30 px-5 relative overflow-hidden">
      <Seo title="Care For Me | Admin" path="/admin/login" noindex />
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 bg-brand-soft rounded-full blur-3xl" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-brand font-semibold mb-6 hover:text-brand-hover transition-colors">
          <ArrowLeft className="h-4 w-4" /> Care For Me
        </Link>
        <form onSubmit={submit} className="bg-white rounded-[2rem] border border-brand/10 shadow-[0_20px_60px_rgba(134,77,99,0.12)] p-8 sm:p-10" data-testid="admin-login-form">
          <span className="grid place-items-center h-14 w-14 rounded-2xl bg-brand text-brand-cream mb-5">
            <PawPrint className="h-7 w-7" />
          </span>
          <h1 className="font-heading text-3xl font-bold text-brand-ink">{a.login_title}</h1>
          <p className="mt-1 text-brand-ink/60">{a.login_sub}</p>

          <div className="mt-7 space-y-4">
            <div>
              <Label className="text-brand-ink font-semibold">{a.email}</Label>
              <Input data-testid="admin-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl border-brand/20 h-11 focus-visible:ring-brand" />
            </div>
            <div>
              <Label className="text-brand-ink font-semibold">{a.password}</Label>
              <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl border-brand/20 h-11 focus-visible:ring-brand" />
            </div>
          </div>

          {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-destructive font-semibold">{error}</p>}

          <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="mt-7 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base">
            <LogIn className="h-4 w-4 mr-2" />
            {loading ? a.logging : a.login}
          </Button>
        </form>
      </div>
    </div>
  );
}
